import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CLASS_TYPES, BHANGRA_INSTRUCTORS, GIDHA_INSTRUCTORS, DHOL_INSTRUCTORS, BOLLYWOOD_INSTRUCTORS } from '../../utils/constants';
import { createInstructor, updateInstructor, deleteInstructor, fetchInstructors } from '../../store/slices/instructorsSlice';

const InstructorList = ({ isEditing, classType }) => {
  const dispatch = useDispatch();
  const { instructors, loading } = useSelector((state) => state.instructors);
  
  // Fetch instructors when component mounts or when classType changes
  useEffect(() => {
    dispatch(fetchInstructors());
  }, [dispatch, classType]);
  
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newInstructor, setNewInstructor] = useState({
    name: '',
    classTypes: [],
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Get fallback instructors from constants.js if database is empty
  const getFallbackInstructors = (type) => {
    switch (type) {
      case 'Bhangra':
        return BHANGRA_INSTRUCTORS.map(name => ({ name, classTypes: ['Bhangra'], isActive: true, id: `fallback-${name}` }));
      case 'Gidha':
        return GIDHA_INSTRUCTORS.map(name => ({ name, classTypes: ['Gidha'], isActive: true, id: `fallback-${name}` }));
      case 'Dhol':
        return DHOL_INSTRUCTORS.map(name => ({ name, classTypes: ['Dhol'], isActive: true, id: `fallback-${name}` }));
      case 'Bollywood':
        return BOLLYWOOD_INSTRUCTORS.map(name => ({ name, classTypes: ['Bollywood'], isActive: true, id: `fallback-${name}` }));
      default:
        return [];
    }
  };

  // Filter instructors by class type if provided
  // If database is empty, use fallback from constants.js
  let filteredInstructors = [];
  if (instructors.length === 0 && classType) {
    // Use fallback from constants.js if database is empty
    filteredInstructors = getFallbackInstructors(classType);
  } else {
    filteredInstructors = classType
      ? instructors.filter((instructor) => 
          instructor.isActive !== false && 
          instructor.classTypes && 
          instructor.classTypes.includes(classType)
        )
      : instructors.filter((instructor) => instructor.isActive !== false);
  }

  const handleAddInstructor = async () => {
    if (!newInstructor.name.trim()) {
      alert('Please enter an instructor name');
      return;
    }

    if (newInstructor.classTypes.length === 0) {
      alert('Please select at least one class type');
      return;
    }

    try {
      await dispatch(createInstructor({
        name: newInstructor.name.trim(),
        classTypes: newInstructor.classTypes,
        isActive: true,
      }));
      await dispatch(fetchInstructors()); // Refresh list
      setNewInstructor({ name: '', classTypes: [] });
      setShowAddForm(false);
    } catch (error) {
      alert('Error adding instructor: ' + (error.message || 'Unknown error'));
    }
  };

  const handleUpdateInstructor = async (instructorId, field, value) => {
    try {
      const instructor = instructors.find((i) => i.id === instructorId || i._id === instructorId);
      if (!instructor) return;

      const updatedData = { ...instructor };
      if (field === 'name') {
        updatedData.name = value;
      } else if (field === 'classTypes') {
        updatedData.classTypes = value;
      }

      await dispatch(updateInstructor({ id: instructorId, data: updatedData }));
      await dispatch(fetchInstructors()); // Refresh list
      setEditingId(null);
    } catch (error) {
      alert('Error updating instructor: ' + (error.message || 'Unknown error'));
    }
  };

  const handleDeleteInstructor = async (instructorId) => {
    try {
      await dispatch(deleteInstructor(instructorId));
      await dispatch(fetchInstructors()); // Refresh list
      setDeleteConfirm(null);
    } catch (error) {
      alert('Error deleting instructor: ' + (error.message || 'Unknown error'));
    }
  };

  const handleClassTypeToggle = (instructorId, classType) => {
    const instructor = instructors.find((i) => i.id === instructorId || i._id === instructorId);
    if (!instructor) return;

    const currentTypes = instructor.classTypes || [];
    const newTypes = currentTypes.includes(classType)
      ? currentTypes.filter((t) => t !== classType)
      : [...currentTypes, classType];

    handleUpdateInstructor(instructorId, 'classTypes', newTypes);
  };

  const startEdit = (instructor) => {
    // If it's a fallback instructor (from constants.js), we need to create it in the database first
    if (instructor.id && instructor.id.startsWith('fallback-')) {
      // Create the instructor in the database
      handleAddInstructorFromFallback(instructor);
      return;
    }
    setEditingId(instructor.id || instructor._id);
    setNewInstructor({
      name: instructor.name,
      classTypes: instructor.classTypes || [],
    });
    setShowAddForm(true);
  };

  const handleAddInstructorFromFallback = async (instructor) => {
    try {
      await dispatch(createInstructor({
        name: instructor.name,
        classTypes: instructor.classTypes || [],
        isActive: true,
      }));
      await dispatch(fetchInstructors()); // Refresh list
      // Now start editing the newly created instructor
      setTimeout(() => {
        dispatch(fetchInstructors()).then(() => {
          const updatedInstructors = instructors.filter(i => i.name === instructor.name);
          if (updatedInstructors.length > 0) {
            startEdit(updatedInstructors[0]);
          }
        });
      }, 500);
    } catch (error) {
      alert('Error creating instructor: ' + (error.message || 'Unknown error'));
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setShowAddForm(false);
    setNewInstructor({ name: '', classTypes: [] });
  };

  if (loading && instructors.length === 0) {
    return <div className="text-sm text-gray-500 py-4 text-center">Loading instructors...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-semibold text-gray-800">
          Instructors {classType && `(${classType})`} ({filteredInstructors.length})
        </h3>
        {isEditing && (
          <button
            onClick={() => {
              setShowAddForm(!showAddForm);
              setEditingId(null);
              setNewInstructor({ name: '', classTypes: [] });
            }}
            className="bg-orange-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-lg font-semibold hover:bg-orange-700 transition-colors"
          >
            +
          </button>
        )}
      </div>

      {/* Add/Edit Instructor Form */}
      {showAddForm && isEditing && (
        <div className="mb-3 p-3 bg-gray-50 rounded border border-gray-200">
          <input
            type="text"
            value={newInstructor.name}
            onChange={(e) => setNewInstructor({ ...newInstructor, name: e.target.value })}
            placeholder="Instructor name"
            className="w-full mb-2 border border-gray-300 rounded px-2 py-1 text-sm"
            disabled={!!editingId}
          />
          <div className="mb-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Class Types</label>
            <div className="grid grid-cols-2 gap-2">
              {CLASS_TYPES.map((type) => (
                <label
                  key={type}
                  className="flex items-center space-x-2 p-1.5 border border-gray-300 rounded hover:bg-gray-100 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={newInstructor.classTypes.includes(type)}
                    onChange={(e) => {
                      const newTypes = e.target.checked
                        ? [...newInstructor.classTypes, type]
                        : newInstructor.classTypes.filter((t) => t !== type);
                      setNewInstructor({ ...newInstructor, classTypes: newTypes });
                    }}
                    className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                  <span className="text-xs text-gray-700">{type}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={editingId ? () => handleUpdateInstructor(editingId, 'name', newInstructor.name) : handleAddInstructor}
              className="flex-1 bg-orange-600 text-white px-3 py-1 rounded text-sm hover:bg-orange-700 transition-colors"
            >
              {editingId ? 'Update' : 'Add'}
            </button>
            <button
              onClick={cancelEdit}
              className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Instructor List */}
      <div className="space-y-1.5 max-h-96 overflow-y-auto">
        {filteredInstructors.length === 0 ? (
          <p className="text-sm text-gray-500 py-4 text-center">No instructors added yet</p>
        ) : (
          filteredInstructors.map((instructor) => {
            const instructorId = instructor.id || instructor._id;
            const isCurrentlyEditing = editingId === instructorId;

            return (
              <div
                key={instructorId}
                className="flex items-center gap-2 p-2 bg-gray-50 rounded border border-gray-200 hover:bg-gray-100 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{instructor.name}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {instructor.classTypes && instructor.classTypes.length > 0 ? (
                      instructor.classTypes.map((type) => (
                        <span
                          key={type}
                          className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800"
                        >
                          {type}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-gray-500">No class types assigned</span>
                    )}
                  </div>
                </div>
                {isEditing && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => startEdit(instructor)}
                      className="p-1 text-gray-600 hover:text-orange-600 transition-colors"
                      aria-label="Edit"
                      title={instructor.id && instructor.id.startsWith('fallback-') ? 'Add to database to edit' : 'Edit instructor'}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    {!(instructor.id && instructor.id.startsWith('fallback-')) && (
                      <button
                        onClick={() => setDeleteConfirm({ id: instructorId, name: instructor.name })}
                        className="p-1 text-gray-600 hover:text-red-600 transition-colors"
                        aria-label="Delete"
                        title="Delete instructor"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-4 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-2">Delete Instructor?</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This will set them as inactive.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => handleDeleteInstructor(deleteConfirm.id)}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded text-sm hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstructorList;

